// ============================================================
// Config — resolve user config with defaults, localStorage persistence
// ============================================================
import type { PlayerConfig, ResolvedConfig, QualityLevel } from './types';

const STORAGE_KEY = 'ggplayer_v4_settings';

const PERSISTED_KEYS = ['volume', 'muted', 'quality', 'latencyMode'] as const;

interface PersistedSettings {
  volume?: number;
  muted?: boolean;
  quality?: QualityLevel;
  latencyMode?: 'low' | 'normal';
}

export function resolveConfig(input: PlayerConfig): ResolvedConfig {
  const saved = loadSaved();
  return {
    container: input.container,
    streamKey: input.streamKey,
    autoplay: input.autoplay ?? false,
    muted: input.muted ?? saved.muted ?? false,
    volume: input.volume ?? saved.volume ?? 0.3,
    quality: saved.quality ?? input.quality ?? 'auto',
    poster: input.poster,
    previewMode: input.previewMode ?? false,
    lowLatency: input.lowLatency ?? 'auto',
    latencyFallback: input.latencyFallback ?? true,
    showDebug: input.showDebug ?? true,
    hlsUrl: input.hlsUrl ?? 'https://hls.goodgame.ru',
    sources: input.sources,
    apiData: input.apiData,
    plugins: input.plugins ?? [],
    onReady: input.onReady,
    onError: input.onError,
  };
}

/** Build master playlist URL from config. */
export function resolveStreamUrl(config: ResolvedConfig): string {
  // Priority 1: explicit sources
  if (config.sources?.master) return config.sources.master;
  // Priority 2: API data
  if (config.apiData?.sources?.master) return config.apiData.sources.master;
  if (config.apiData?.sources?.source) return config.apiData.sources.source;
  // Priority 3: build from streamKey
  return `${config.hlsUrl}/manifest/${config.streamKey}_master.m3u8`;
}

/** Extract query params string from a URL (for secure link propagation). */
export function extractQueryParams(url: string): string {
  const idx = url.indexOf('?');
  return idx !== -1 ? url.substring(idx + 1) : '';
}

/** Save user-changeable settings to localStorage (merges with existing). */
export function saveSettings(settings: Partial<PersistedSettings>): void {
  try {
    const existing = loadSaved();
    const merged = { ...existing, ...settings };
    const persisted = PERSISTED_KEYS.reduce<PersistedSettings>((result, key) => {
      const value = merged[key];
      if (value !== undefined) Object.assign(result, { [key]: value });
      return result;
    }, {});
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    // localStorage may be unavailable (incognito, quota)
  }
}

/** Read the user-selected latency mode without exposing it as player configuration. */
export function getSavedLatencyMode(): 'low' | 'normal' | undefined {
  return loadSaved().latencyMode;
}

/** Load persisted settings. */
function loadSaved(): PersistedSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistedSettings;
      return parsed.latencyMode === 'low' || parsed.latencyMode === 'normal'
        ? parsed
        : { ...parsed, latencyMode: undefined };
    }
    return {};
  } catch {
    return {};
  }
}
